import WarningAmber from "@mui/icons-material/WarningAmber";
import {
	Box,
	MenuItem,
	Select,
	SelectChangeEvent,
	Skeleton,
	Switch,
	Typography,
} from "@mui/material";
import React, { useMemo } from "react";

import { colors } from "../../../../../../config/colors";
import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import AllowedWorkcenters from "../flex-signup/allowed-workcenters";

interface WorkCenter {
	id: string | number;
	name: string;
	isFlexiSignupEnabled: boolean;
}

interface FlexiShiftSignupProps {
	newShiftSettings: {
		flexiShiftAllowed: boolean;
		minFlexiShiftHours: number;
	};
	setNewShiftSettings: React.Dispatch<React.SetStateAction<any>>;
	loading: boolean;
	flexiShiftWorkcenters: any;
	newFlexiShiftWorkcenters: any;
	setNewFlexiShiftWorkcenters: React.Dispatch<React.SetStateAction<any>>;
}

const FlexiShiftSignup: React.FC<FlexiShiftSignupProps> = ({
	newShiftSettings,
	setNewShiftSettings,
	loading,
	flexiShiftWorkcenters,
	newFlexiShiftWorkcenters,
	setNewFlexiShiftWorkcenters,
}) => {
	const {
		getLocationSettingsStatus,
		currentLocationData,
		updateAllowedWorkCentersForShiftsStatus,
		minShiftDurationHours,
		maxShiftDurationHours,
	} = useGatAdminConfigData();

	const { wc, locationWorkcenters } = useMemo(() => {
		if (!currentLocationData?.workCenters) {
			return { wc: {}, locationWorkcenters: [] };
		}

		const wc = Object.fromEntries(
			currentLocationData.workCenters.map((wc: WorkCenter) => [wc.id, wc.name])
		);

		return {
			wc,
			locationWorkcenters: currentLocationData.workCenters,
		};
	}, [currentLocationData?.workCenters]);

	const handleWorkcenterChange = (
		event: SelectChangeEvent<typeof flexiShiftWorkcenters>
	) => {
		const value = event.target.value;
		if (value.includes("all")) {
			if (newFlexiShiftWorkcenters.length === locationWorkcenters.length) {
				setNewFlexiShiftWorkcenters([]);
			} else {
				setNewFlexiShiftWorkcenters(
					locationWorkcenters?.map((wc: { id: number }) => wc.id) || []
				);
			}
		} else {
			setNewFlexiShiftWorkcenters(value as string[]);
		}
	};

	const handleDelete = (
		event: React.MouseEvent,
		workcenterToDelete: string
	) => {
		event.stopPropagation();
		if (!newShiftSettings.flexiShiftAllowed) {
			return;
		}
		setNewFlexiShiftWorkcenters(
			newFlexiShiftWorkcenters.filter(
				(wc: string) => wc !== workcenterToDelete
			)
		);
	};
	return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        borderRadius: "8px",
        marginTop: "32px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "16px",
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            Allow Workers to sign up for custom duration of the Shift
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "12px",
              color: "rgba(0, 0, 0, 0.6)",
            }}
          >
            Enable Workers to pick up any duration of the Shift as long as
            it&apos;s more than the minimum Shift duration
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading || getLocationSettingsStatus === "pending" ? (
            <Skeleton width={50} height={40} />
          ) : (
            <Switch
              checked={newShiftSettings.flexiShiftAllowed ?? false}
              onChange={(e) => {
                setNewShiftSettings((prevSettings: any) => ({
                  ...prevSettings,
                  flexiShiftAllowed: e.target.checked,
                  minFlexiShiftHours: e.target.checked
                    ? minShiftDurationHours
                    : prevSettings.minFlexiShiftHours,
                }));
              }}
            />
          )}
        </Box>
      </Box>

      {newShiftSettings.flexiShiftAllowed ? (
        <Box
          sx={{
            marginTop: "32px",
          }}
        >
          <Box
            sx={{
              marginTop: "20px",
              marginBottom: "8px",
              borderRadius: "8px",
              border: 1,
              borderWidth: "1px",
              borderColor: "rgba(0,0,0,0.12)",
              background: "#FFF",
              padding: "16px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              {Array.isArray(locationWorkcenters) &&
              locationWorkcenters.length > 0 ? (
                <AllowedWorkcenters
                  loading={
                    loading ||
                    updateAllowedWorkCentersForShiftsStatus === "pending" ||
                    getLocationSettingsStatus === "pending"
                  }
                  selectedWorkcenters={newFlexiShiftWorkcenters}
                  handleWorkcenterChange={handleWorkcenterChange}
                  handleDelete={handleDelete}
                  locationWorkcenters={locationWorkcenters}
                  wc={wc}
                  updateStatus={updateAllowedWorkCentersForShiftsStatus}
                />
              ) : (
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  Please add a workcenter from the Workcenters section to be
                  able to select it for Flexi Shift signup.
                </Typography>
              )}
            </Box>
            {Array.isArray(locationWorkcenters) &&
            locationWorkcenters.length > 0 &&
            newFlexiShiftWorkcenters.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginLeft: "8px",
                  marginTop: "4px",
                }}
              >
                <WarningAmber sx={{ color: colors.error, fontSize: "14px" }} />
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: colors.error,
                  }}
                >
                  Select at least 1 workcenter to save.
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "rgba(0, 0, 0, 0.87)",
                  marginLeft: "8px",
                }}
              >
                Minimum Shift duration that allows such signups
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginLeft: "8px",
              }}
            >
              {getLocationSettingsStatus === "pending" ? (
                <Skeleton width={60} height={50} />
              ) : (
                <Select
                  id="flexi-shift-min-duration"
                  variant="filled"
                  sx={{
                    "& .MuiInputBase-input": {
                      background: "#FFF",
                      padding: "8px",
                    },
                    width:
                      newShiftSettings.minFlexiShiftHours > 90
                        ? "70px"
                        : "60px",
                  }}
                  onChange={(e) => {
                    setNewShiftSettings((prevSettings: any) => ({
                      ...prevSettings,
                      minFlexiShiftHours: Number(e.target.value),
                    }));
                  }}
                  value={
                    newShiftSettings.minFlexiShiftHours ?? minShiftDurationHours
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 120,
                        overflow: "auto",
                      },
                    },
                  }}
                >
                  {Array.from(
                    {
                      length: maxShiftDurationHours - minShiftDurationHours + 1,
                    },
                    (_, i) => (
                      <MenuItem key={i} value={minShiftDurationHours + i}>
                        {minShiftDurationHours + i}
                      </MenuItem>
                    )
                  )}
                </Select>
              )}
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "rgba(0, 0, 0, 0.87)",
                  marginLeft: "8px",
                }}
              >
                hours
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default FlexiShiftSignup;
